import * as contracts from "../contracts/contracts";
import { WindowBounds } from "../contracts/windowBounds";

import WindowType from "./windowType";
import ActivityEntity from "./activityEntity";
import Activity from "./activity";

import { Logger } from "../helpers/logger";
import * as util from "../helpers/util";
import { ifNotUndefined } from "../helpers/util";
import { EntityEventType } from "../contracts/entityEvent";
import { Glue42Core } from "@glue42/core";
import { Glue42 } from "../../../glue";

/**
 * A window participating in an activity
 *
 * @module activityWindow
 */
export default class ActivityWindow extends ActivityEntity {

    private _logger: Logger = Logger.Get("window");

    private _type: string;
    private _activityId: string;
    private _name: string;
    private _instance: Glue42Core.AGM.Instance;
    private _isIndependent: boolean;
    private _windowGetter: () => Glue42.Windows.GDWindow;
    private _hcWindowId: string;

    constructor(id: string, name: string, type: string, activityId: string, instance: Glue42Core.AGM.Instance, isIndependent: boolean, windowGetter: any,
                hcWindowId: string) {
        super(id);
        this._type = type;
        this._activityId = activityId;
        this._name = name;
        this._instance = instance;
        this._isIndependent = isIndependent;
        this._windowGetter = windowGetter;
        this._hcWindowId = hcWindowId;
    }

    /**
     * Return window's title
     * @function getBounds
     * @return {Promise<WindowBounds>}
     */
    public getBounds(): Promise<WindowBounds> {
        return this._manager.getWindowBounds(this.id);
    }

    /**
     * @var {string} name The window name
     */
    public get name(): string {
        return this._name;
    }

    /**
     * @var {bool} isIndependent True if this is an independent window
     */
    public get isIndependent(): boolean {
        return this._isIndependent;
    }

    /**
     * @var {module:windowType} type The window type
     */
    public get type(): WindowType {
        if (this._manager) {
            return this._manager.getWindowType(this._type);
        }
        return undefined;
    }

    /**
     * @var {module:activity} activity The activity that the window is part of
     */
    public get activity(): Activity {
        if (util.isUndefined(this._activityId)) {
            return undefined;
        }
        return this._manager.getActivityById(this._activityId);
    }

    /**
     * @var {bool} isOwner True if the window is the owner of the activity.
     */
    public get isOwner(): boolean {
        const act = this.activity;
        if (util.isUndefined(act)) {
            return false;
        }

        return act.owner.id === this.id;
    }

    /**
     * Sets window's visibility
     *
     * @function setVisible
     * @param {boolean} isVisible
     * @returns {Promise<module:activityWindow>}
     */
    public setVisible(isVisible: boolean, callback?: contracts.Callback<ActivityWindow>): Promise<ActivityWindow> {
        return this._manager.setWindowVisibility(this.id, isVisible);
    }

    /**
     * Activate window
     * @function activate
     * @param {boolean} focus If true then activate window with focus otherwise activate window without focus
     */
    public activate(focus: boolean): Promise<ActivityWindow> {
        return this._manager.activateWindow(this.id, focus);
    }

    /**
     * Sets window's bounds
     * @function setBounds
     *
     * @param {bounds} bounds The new bounds to be applied
     * @returns  {Promise<module:activityWindow>}
     */
    public setBounds(bounds: WindowBounds, callback?: contracts.Callback<ActivityWindow>): Promise<ActivityWindow> {
        return this._manager.setWindowBounds(this.id, bounds, callback);
    }

    /**
     * Closes the window
     *
     * @function close
     * @returns {Promise}
     */
    public close(): Promise<any> {
        return this._manager.closeWindow(this.id);
    }

    /**
     * @var {agmInstance} instance The agm instance of that window. You can use this to invoke AGM methods against that window
     */
    public get instance(): Glue42Core.AGM.Instance {
        return this._instance;
    }

    /**
     * Returns the window as object from Windows API (glue.windows).
     *
     * @var {window} underlyingWindow
     */
    public get underlyingWindow(): any {
        const window = this._windowGetter();
        if (!window) {
            // we don't have the window object any more, let's return as much as we can
            return {
                id: this._hcWindowId
            };
        }
        return window;
    }

    public onActivityJoined(callback: (activity: Activity) => void) {
        this._subscribeForActivityWindowEvent(EntityEventType.ActivityWindowJoinedActivity, callback);
    }

    public onActivityRemoved(callback: (activity: Activity) => void) {
        this._subscribeForActivityWindowEvent(EntityEventType.ActivityWindowLeftActivity, callback);
    }

    public _updateCore(other: ActivityWindow) {
        ifNotUndefined(other._activityId, (x) => this._activityId = x);
        ifNotUndefined(other._isIndependent, (x) => this._isIndependent = x);
        ifNotUndefined(other._hcWindowId, (x) => this._hcWindowId = x);
        ifNotUndefined(other._type, (x) => this._type = x);
        ifNotUndefined(other._name, (x) => this._name = x);

        if (!util.isUndefinedOrNull(other._instance)) {
            this._instance = other._instance;
        }
    }

    public _subscribeForActivityWindowEvent(eventName: string, callback: (activity: Activity) => void) {
        this._manager.subscribeWindowEvents((activity, window, event) => {
            // ignore events not related to this window
            if (window.id !== this.id) {
                return;
            }

            if (event === eventName) {
                callback(activity);
            }
        });
    }

    public _beforeDelete(other: ActivityWindow) {
        this._hcWindowId = other._hcWindowId;
    }
}
